// @ts-check
import { AlertTriangle, Edit, Globe, Plus, Save, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import Modal from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import { useRegisterUnsavedChanges } from '../context/UnsavedChangesContext';
import api from '../utils/apiClient';

const PricingConfigPage = () => {
  const toast = useToast();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCode, setEditingCode] = useState(null);
  // Touched-since-edit-started flag for the inline row editor, reset by
  // cancelEdit/saveEdit. startEdit populates via the raw setter so loading a
  // row for editing never itself counts as an edit.
  const [editIsDirty, setEditIsDirty] = useState(false);
  useRegisterUnsavedChanges(editIsDirty);
  /** @type {[{slug?: string, currency?: string, currency_symbol?: string, tariff_markup_pct?: number, is_active?: boolean, notes?: string}, Function]} */
  const [editForm, setEditFormRaw] = useState({});
  const setEditForm = useCallback((updater) => {
    setEditIsDirty(true);
    setEditFormRaw(updater);
  }, []);
  const [showAddModal, setShowAddModal] = useState(false);
  // Touched-since-open flag for the Add Country modal, reset after a
  // successful add and on cancel.
  const [addIsDirty, setAddIsDirty] = useState(false);
  /** @type {[{country_code: string, country_name: string, slug: string, currency: string, currency_symbol: string, tariff_markup_pct: number, is_active: boolean, notes: string}, Function]} */
  const [addForm, setAddFormRaw] = useState({
    country_code: '',
    country_name: '',
    slug: '',
    currency: '',
    currency_symbol: '',
    tariff_markup_pct: 0,
    is_active: true,
    notes: '',
  });
  const setAddForm = useCallback((updater) => {
    setAddIsDirty(true);
    setAddFormRaw(updater);
  }, []);
  const [saving, setSaving] = useState(false);

  const isStale = (updatedAt) => {
    if (!updatedAt) return true;
    return Date.now() - new Date(updatedAt).getTime() > 30 * 24 * 60 * 60 * 1000;
  };

  const loadConfigs = useCallback(async () => {
    try {
      const data = await api.get('/api/admin/pricing/country-config');
      setConfigs(data);
    } catch (error) {
      toast.error('Failed to load pricing configs');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const startEdit = (config) => {
    setEditingCode(config.country_code);
    setEditFormRaw({
      slug: config.slug || '',
      currency: config.currency || '',
      currency_symbol: config.currency_symbol || '',
      tariff_markup_pct: config.tariff_markup_pct,
      is_active: config.is_active,
      notes: config.notes || '',
    });
  };

  const cancelEdit = () => {
    setEditingCode(null);
    setEditFormRaw({});
    setEditIsDirty(false);
  };

  // The row's Cancel/X button — confirm first if there's anything to lose.
  const handleCancelEditClick = () => {
    if (editIsDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    cancelEdit();
  };

  const saveEdit = async (countryCode) => {
    setSaving(true);
    try {
      await api.put(`/api/admin/pricing/country-config/${countryCode}`, editForm);
      toast.success(`Updated tariff for ${countryCode}`);
      setEditingCode(null);
      setEditIsDirty(false);
      await loadConfigs();
    } catch (error) {
      toast.error(error.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!addForm.country_code || !addForm.country_name) {
      toast.error('Country code and name are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/admin/pricing/country-config', {
        ...addForm,
        country_code: addForm.country_code.toUpperCase(),
      });
      toast.success(`Added tariff config for ${addForm.country_code.toUpperCase()}`);
      setShowAddModal(false);
      setAddFormRaw({
        country_code: '',
        country_name: '',
        slug: '',
        currency: '',
        currency_symbol: '',
        tariff_markup_pct: 0,
        is_active: true,
        notes: '',
      });
      setAddIsDirty(false);
      await loadConfigs();
    } catch (error) {
      toast.error(error.message || 'Failed to add');
    } finally {
      setSaving(false);
    }
  };

  // Add Country modal's X/Cancel — confirm first if there's anything to lose.
  const handleAddModalClose = () => {
    if (addIsDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setShowAddModal(false);
  };

  const handleDelete = async (countryCode) => {
    if (!window.confirm(`Delete tariff config for ${countryCode}? Orders from this country will default to 0% tariff.`))
      return;
    try {
      await api.del(`/api/admin/pricing/country-config/${countryCode}`);
      toast.success(`Deleted config for ${countryCode}`);
      await loadConfigs();
    } catch (error) {
      toast.error(error.message || 'Failed to delete');
    }
  };

  if (loading) return <LoadingScreen />;

  const staleConfigs = configs.filter((c) => c.is_active && isStale(c.updated_at));

  return (
    <div>
      <div className="flex items-start gap-3 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-900 rounded-lg px-4 py-3 mb-6 text-sm text-blue-800 dark:text-blue-300">
        <Globe size={18} className="mt-0.5 shrink-0 text-blue-500" />
        <span>
          This list also drives the tag-printer CSV's country picker (price + link). Editing it does not change the live
          storefront's routing or currency support (frontend-next keeps its own separate list) - adding a country here
          that doesn't already exist there will produce tag links the storefront can't resolve.
        </span>
      </div>
      {staleConfigs.length > 0 && (
        <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 rounded-lg px-4 py-3 mb-6 text-sm text-amber-800 dark:text-amber-300">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-500" />
          <span>
            <strong>
              {staleConfigs.length} {staleConfigs.length === 1 ? 'country' : 'countries'}
            </strong>{' '}
            ({staleConfigs.map((c) => c.country_code).join(', ')}) {staleConfigs.length === 1 ? 'has' : 'have'} tariff
            rates that haven't been reviewed in over 30 days. Please verify they're still accurate.
          </span>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="text-3xl font-serif">Pricing Config</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage country-level tariff markup percentages. These are applied on top of exchange-rate-converted prices
            (DDP pricing).
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} />
          Add Country
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-card p-4 border rounded-lg border-border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400">
              <Globe size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Countries Configured</p>
              <p className="text-2xl font-bold">{configs.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-card p-4 border rounded-lg border-border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-50 dark:bg-green-950/50 text-green-600 dark:text-green-400">
              <Globe size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Configs</p>
              <p className="text-2xl font-bold">{configs.filter((c) => c.is_active).length}</p>
            </div>
          </div>
        </div>
        <div
          className={`bg-card p-4 border rounded-lg shadow-sm ${staleConfigs.length > 0 ? 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50' : 'border-border'}`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-lg ${staleConfigs.length > 0 ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400' : 'bg-muted/50 text-muted-foreground/70'}`}
            >
              <AlertTriangle size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Needs Review</p>
              <p
                className={`text-2xl font-bold ${staleConfigs.length > 0 ? 'text-amber-700 dark:text-amber-300' : ''}`}
              >
                {staleConfigs.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border rounded-lg border-border shadow-sm overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Country</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Code</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Slug</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Currency</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Tariff Markup</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Notes</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Updated</th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {configs.map((config) => (
              <tr
                key={config.country_code}
                className={`hover:bg-muted/50 ${config.is_active && isStale(config.updated_at) ? 'bg-amber-50/40' : ''}`}
              >
                <td className="px-4 py-3 font-medium">
                  <div className="flex items-center gap-2">
                    {config.country_name}
                    {config.is_active && isStale(config.updated_at) && (
                      <span
                        title="Rate not reviewed in 30+ days"
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                      >
                        <AlertTriangle size={10} />
                        Stale
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{config.country_code}</td>
                <td className="px-4 py-3 text-muted-foreground">
                  {editingCode === config.country_code ? (
                    <input
                      type="text"
                      aria-label="Slug"
                      value={editForm.slug}
                      onChange={(e) => setEditForm((f) => ({ ...f, slug: e.target.value }))}
                      className="w-16 border border-input rounded-lg px-2 py-1 text-sm focus:outline-none focus:border-foreground"
                    />
                  ) : (
                    config.slug || '-'
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {editingCode === config.country_code ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        aria-label="Currency"
                        value={editForm.currency}
                        onChange={(e) => setEditForm((f) => ({ ...f, currency: e.target.value }))}
                        className="w-16 border border-input rounded-lg px-2 py-1 text-sm focus:outline-none focus:border-foreground"
                      />
                      <input
                        type="text"
                        aria-label="Currency Symbol"
                        value={editForm.currency_symbol}
                        onChange={(e) => setEditForm((f) => ({ ...f, currency_symbol: e.target.value }))}
                        className="w-10 border border-input rounded-lg px-2 py-1 text-sm focus:outline-none focus:border-foreground"
                      />
                    </div>
                  ) : (
                    [config.currency, config.currency_symbol].filter(Boolean).join(' ') || '-'
                  )}
                </td>
                <td className="px-4 py-3">
                  {editingCode === config.country_code ? (
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={editForm.tariff_markup_pct}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, tariff_markup_pct: parseFloat(e.target.value) || 0 }))
                      }
                      className="w-20 border border-input rounded-lg px-2 py-1 text-sm focus:outline-none focus:border-foreground"
                    />
                  ) : (
                    <span className={config.tariff_markup_pct > 0 ? 'font-semibold' : 'text-muted-foreground/70'}>
                      {config.tariff_markup_pct}%
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {editingCode === config.country_code ? (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.is_active}
                        onChange={(e) => setEditForm((f) => ({ ...f, is_active: e.target.checked }))}
                        className="w-4 h-4 accent-black"
                      />
                      <span className="text-xs">{editForm.is_active ? 'Active' : 'Inactive'}</span>
                    </label>
                  ) : (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                        config.is_active
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {config.is_active ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs max-w-xs truncate">
                  {editingCode === config.country_code ? (
                    <input
                      type="text"
                      value={editForm.notes}
                      onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                      className="w-full border border-input rounded-lg px-2 py-1 text-sm focus:outline-none focus:border-foreground"
                      placeholder="Notes"
                    />
                  ) : (
                    config.notes || '-'
                  )}
                </td>
                <td
                  className={`px-4 py-3 text-xs ${config.is_active && isStale(config.updated_at) ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-muted-foreground/70'}`}
                >
                  {config.updated_at
                    ? new Date(config.updated_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })
                    : 'Never'}
                </td>
                <td className="px-4 py-3 text-right">
                  {editingCode === config.country_code ? (
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => saveEdit(config.country_code)}
                        disabled={saving}
                        className="p-1.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
                        title="Save"
                      >
                        <Save size={14} />
                      </button>
                      <button
                        onClick={handleCancelEditClick}
                        className="p-1.5 text-muted-foreground hover:text-foreground"
                        title="Cancel"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => startEdit(config)}
                        className="p-1.5 text-muted-foreground/70 hover:text-foreground"
                        title="Edit"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(config.country_code)}
                        className="p-1.5 text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {configs.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Globe size={48} className="mx-auto mb-4 text-muted-foreground/50" />
            <p>No country pricing configs yet.</p>
            <p className="text-sm">Add your first country to start applying tariff markups.</p>
          </div>
        )}
      </div>

      {/* Add Country Modal */}
      {showAddModal && (
        <Modal
          maxWidth="md"
          fullHeight={false}
          onClose={handleAddModalClose}
          className="rounded-lg shadow-xl p-6"
          closeOnBackdrop={false}
          hasUnsavedChanges={addIsDirty}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-serif">Add Country Tariff</h2>
            <button onClick={handleAddModalClose} className="p-1 hover:bg-accent rounded-lg">
              <X size={20} />
            </button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Country Code</label>
                <input
                  type="text"
                  maxLength={5}
                  value={addForm.country_code}
                  onChange={(e) => setAddForm((f) => ({ ...f, country_code: e.target.value.toUpperCase() }))}
                  placeholder="US"
                  className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-foreground"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Country Name</label>
                <input
                  type="text"
                  value={addForm.country_name}
                  onChange={(e) => setAddForm((f) => ({ ...f, country_name: e.target.value }))}
                  placeholder="United States"
                  className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-foreground"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Slug</label>
                <input
                  type="text"
                  value={addForm.slug}
                  onChange={(e) => setAddForm((f) => ({ ...f, slug: e.target.value.toLowerCase() }))}
                  placeholder="us"
                  className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-foreground"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Currency</label>
                <input
                  type="text"
                  value={addForm.currency}
                  onChange={(e) => setAddForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))}
                  placeholder="USD"
                  className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-foreground"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Currency Symbol</label>
                <input
                  type="text"
                  value={addForm.currency_symbol}
                  onChange={(e) => setAddForm((f) => ({ ...f, currency_symbol: e.target.value }))}
                  placeholder="$"
                  className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-foreground"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Tariff Markup (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={addForm.tariff_markup_pct}
                onChange={(e) => setAddForm((f) => ({ ...f, tariff_markup_pct: parseFloat(e.target.value) || 0 }))}
                className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-foreground"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Notes</label>
              <input
                type="text"
                value={addForm.notes}
                onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="e.g. EU common external tariff"
                className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-foreground"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={addForm.is_active}
                onChange={(e) => setAddForm((f) => ({ ...f, is_active: e.target.checked }))}
                className="w-4 h-4 accent-black"
              />
              <span className="text-sm">Active</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={handleAddModalClose}
              className="px-4 py-2 border border-input rounded-lg text-sm hover:bg-muted/50"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={saving}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Adding...' : 'Add Country'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PricingConfigPage;
